import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  CheckCircle2, Clock, CircleDashed, Ban, MousePointer2, ArrowRight,
  ChevronRight, Sparkles, ListChecks, Rocket, Building2, FlaskConical,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const PRIORITY_STYLES = {
  High: 'bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300',
  Medium: 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300',
  Critical: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  Low: 'bg-sky-100 text-sky-700 dark:bg-sky-400/15 dark:text-sky-300',
};

function StatusChip({ status }) {
  const map = {
    Completed: { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300', icon: CheckCircle2, label: 'Completed' },
    'In Progress': { cls: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300', icon: Clock, label: 'In Progress' },
    Pending: { cls: 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300', icon: CircleDashed, label: 'Pending' },
    Blocked: { cls: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300', icon: Ban, label: 'Blocked' },
  };
  const s = map[status] || map.Pending;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', s.cls)}>
      <s.icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

function SummaryCard({ icon: Icon, value, label, tone }) {
  const tones = {
    green: 'text-emerald-600 dark:text-emerald-400',
    blue: 'text-sky-600 dark:text-sky-400',
    slate: 'text-slate-500 dark:text-slate-400',
    red: 'text-red-600 dark:text-red-400',
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 dark:border-white/10 dark:bg-white/[0.03]">
      <Icon className={cn('h-4 w-4 shrink-0', tones[tone])} />
      <div className="leading-none">
        <motion.div key={value} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-sm font-bold text-slate-900 dark:text-white">{value}</motion.div>
        <div className="mt-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      </div>
    </div>
  );
}

function TaskRow({ innerRef, name, meta, assignee, priority, status, progress, selected, dimmed }) {
  return (
    <div
      ref={innerRef}
      className={cn(
        'relative grid grid-cols-[1fr_auto] items-center gap-2 rounded-lg border px-3 py-2 transition-colors sm:grid-cols-[1.6fr_0.7fr_0.7fr_0.9fr]',
        selected
          ? 'border-brand bg-brand/[0.06] shadow-sm'
          : 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.02]',
        dimmed && 'opacity-45'
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 truncate text-xs font-semibold text-slate-800 dark:text-slate-100">
          {name}
          {meta && <span className="rounded bg-slate-100 px-1 py-0.5 text-[9px] font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">{meta}</span>}
        </div>
        <div className="mt-0.5 text-[9px] text-slate-400">{assignee}</div>
      </div>
      <span className={cn('hidden justify-self-start rounded px-1.5 py-0.5 text-[9px] font-semibold sm:inline-flex', PRIORITY_STYLES[priority])}>{priority}</span>
      <div className="hidden text-[9px] font-medium text-slate-400 sm:block">{progress || '—'}</div>
      <div className="justify-self-end sm:justify-self-start"><StatusChip status={status} /></div>
    </div>
  );
}

function GroupLabel({ icon: Icon, children, count }) {
  return (
    <div className="mb-1.5 mt-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      <Icon className="h-3 w-3 text-brand" /> {children}
      {count != null && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-400 dark:bg-white/10">{count}</span>}
    </div>
  );
}

export default function OnboardingHeroDemo() {
  const wrapRef = useRef(null);
  const inView = useInView(wrapRef, { amount: 0.3 });
  const reduced = useRef(false);

  // Element refs for the simulated cursor to target.
  const propsRowRef = useRef(null);
  const goSetupRef = useRef(null);
  const apRowRef = useRef(null);

  const [entered, setEntered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [counts, setCounts] = useState({ completed: 18, inprogress: 8, pending: 6, blocked: 2 });
  const [selected, setSelected] = useState(null); // 'properties' | 'apfile' | null
  const [panelKind, setPanelKind] = useState(null); // 'properties' | 'apfile'
  const [propsStatus, setPropsStatus] = useState('In Progress');
  const [apStatus, setApStatus] = useState('Blocked');
  const [apNoteAdded, setApNoteAdded] = useState(false);
  const [cursor, setCursor] = useState({ x: 40, y: 40, clicking: false });
  const [workflow, setWorkflow] = useState(false);
  const [complete, setComplete] = useState(false);

  const aliveRef = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      reduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }, []);

  const sleep = (ms) => new Promise((res) => {
    const id = setTimeout(res, ms);
    // store on a set so we could clear; simplest: rely on aliveRef checks
    return id;
  });

  const tween = (from, to, ms) => new Promise((resolve) => {
    const start = performance.now();
    const frame = (now) => {
      if (!aliveRef.current) return resolve();
      const t = Math.min(1, (now - start) / ms);
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(Math.round(from + (to - from) * eased));
      if (t < 1) requestAnimationFrame(frame);
      else resolve();
    };
    requestAnimationFrame(frame);
  });

  const moveCursorTo = (ref, dx = 0, dy = 0) => {
    const cont = wrapRef.current;
    const el = ref.current;
    if (!cont || !el) return;
    const c = cont.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setCursor({ x: r.left - c.left + r.width / 2 + dx, y: r.top - c.top + r.height / 2 + dy, clicking: false });
  };

  const click = async () => {
    setCursor((c) => ({ ...c, clicking: true }));
    await sleep(180);
    setCursor((c) => ({ ...c, clicking: false }));
  };

  const resetState = () => {
    setProgress(0);
    setCounts({ completed: 18, inprogress: 8, pending: 6, blocked: 2 });
    setSelected(null); setPanelKind(null);
    setPropsStatus('In Progress'); setApStatus('Blocked'); setApNoteAdded(false);
    setWorkflow(false); setComplete(false);
    setCursor({ x: 40, y: 60, clicking: false });
  };

  useEffect(() => {
    if (!inView) { aliveRef.current = false; return; }

    // Reduced motion: render a completed static state, no loop.
    if (reduced.current) {
      setEntered(true); setProgress(100); setComplete(true);
      setPropsStatus('Completed'); setApStatus('Completed');
      setCounts({ completed: 22, inprogress: 0, pending: 0, blocked: 0 });
      return;
    }

    aliveRef.current = true;
    let cancelled = false;
    const alive = () => aliveRef.current && !cancelled;

    const run = async () => {
      while (alive()) {
        resetState();
        setEntered(false);
        await sleep(60); if (!alive()) break;
        setEntered(true);
        await sleep(600); if (!alive()) break;

        // 2. progress 0 -> 65
        await tween(0, 65, 1300); if (!alive()) break;
        await sleep(350); if (!alive()) break;

        // 3. cursor to Properties, click, open panel
        moveCursorTo(propsRowRef);
        await sleep(750); if (!alive()) break;
        await click(); if (!alive()) break;
        setSelected('properties'); setPanelKind('properties');
        await sleep(1900); if (!alive()) break; // read prerequisites/steps

        // 6. cursor to Go to Setup, click -> workflow flash
        moveCursorTo(goSetupRef);
        await sleep(700); if (!alive()) break;
        await click(); if (!alive()) break;
        setWorkflow(true);
        await sleep(1250); if (!alive()) break;
        setWorkflow(false);
        setPanelKind(null); setSelected(null);
        await sleep(250); if (!alive()) break;

        // 8-9. Properties completed, 65 -> 85, counters update
        setPropsStatus('Completed');
        setCounts({ completed: 20, inprogress: 6, pending: 6, blocked: 2 });
        await tween(65, 85, 1000); if (!alive()) break;
        await sleep(500); if (!alive()) break;

        // 10. Highlight blocked AP File Testing, open panel
        moveCursorTo(apRowRef);
        await sleep(700); if (!alive()) break;
        await click(); if (!alive()) break;
        setSelected('apfile'); setPanelKind('apfile');
        await sleep(1500); if (!alive()) break;

        // 11. add update -> Blocked to Completed
        setApNoteAdded(true);
        await sleep(1100); if (!alive()) break;
        setApStatus('Completed');
        setPanelKind(null); setSelected(null);
        setCounts({ completed: 22, inprogress: 4, pending: 4, blocked: 0 });
        await tween(85, 100, 1000); if (!alive()) break;

        // 12-13. completion banner + shimmer
        setComplete(true);
        await sleep(2100); if (!alive()) break;

        // 14. smooth reset pause
        setComplete(false);
        await sleep(500);
      }
    };
    run();
    return () => { cancelled = true; aliveRef.current = false; };
  }, [inView]);

  return (
    <div ref={wrapRef} className="relative w-full" style={{ perspective: '1600px' }} data-testid="onboarding-hero-demo">
      <motion.div
        initial={false}
        animate={entered ? { opacity: 1, scale: 1, y: 0, rotateX: 2, rotateY: -3 } : { opacity: 0, scale: 0.96, y: 24, rotateX: 6, rotateY: -6 }}
        transition={{ type: 'spring', stiffness: 120, damping: 18 }}
        style={{ transformStyle: 'preserve-3d' }}
        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/[0.12] dark:border-white/10 dark:bg-[#0d0d12]"
      >
        {/* App top bar */}
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/80 px-4 py-2.5 dark:border-white/10 dark:bg-white/[0.03]">
          <span className="flex h-5 w-5 items-center justify-center rounded bg-brand text-[10px] font-black text-white">B</span>
          <span className="text-xs font-bold text-slate-800 dark:text-white">Onboarding Planner</span>
          <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">Active</span>
          <div className="ml-auto flex items-center gap-1.5 text-[9px] font-medium text-slate-400">
            <span className="hidden rounded border border-slate-200 px-1.5 py-0.5 dark:border-white/10 sm:inline">Export</span>
            <span className="rounded border border-slate-200 px-1.5 py-0.5 dark:border-white/10">FAQs</span>
          </div>
        </div>

        {/* Dashboard header */}
        <div className="border-b border-slate-200 px-4 py-3 dark:border-white/10">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="flex items-baseline gap-2">
                <motion.span className="font-display text-3xl font-black leading-none text-slate-900 dark:text-white">{progress}%</motion.span>
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Overall completion</span>
              </div>
              <div className="mt-2 h-2 w-44 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10 sm:w-56">
                <motion.div className="h-full rounded-full bg-brand" style={{ width: `${progress}%` }} transition={{ ease: 'easeOut' }} />
              </div>
              <div className="mt-1.5 text-[9px] text-slate-400">Start: 16 Jun 2026 · Target Go-Live: 29 Aug 2026</div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-1.5">
            <SummaryCard icon={CheckCircle2} value={counts.completed} label="Completed" tone="green" />
            <SummaryCard icon={Clock} value={counts.inprogress} label="In Progress" tone="blue" />
            <SummaryCard icon={CircleDashed} value={counts.pending} label="Pending" tone="slate" />
            <SummaryCard icon={Ban} value={counts.blocked} label="Blocked" tone="red" />
          </div>
        </div>

        {/* Task lists */}
        <div className="relative max-h-[300px] overflow-hidden px-4 pb-4">
          <GroupLabel icon={Building2} count={9}>Foundation Setup</GroupLabel>
          <div className="space-y-1.5">
            <TaskRow innerRef={propsRowRef} name="Properties" meta="30/30" assignee="Satish · 18–21 Jun" priority="High" progress="30/30" status={propsStatus} selected={selected === 'properties'} />
            <TaskRow name="Units" assignee="Satish · 20–23 Jun" priority="Low" progress="80/80" status="Completed" />
            <TaskRow name="Accounts (Common Area & Units)" assignee="S. Sreenivasan · 22–25 Jun" priority="Medium" progress="3.5k/3.5k" status="Completed" />
          </div>

          <GroupLabel icon={Rocket} count={6}>Automation</GroupLabel>
          <div className="space-y-1.5">
            <TaskRow name="Bill Stream — Email" assignee="Satish · 04–07 Jul" priority="Critical" status="Completed" />
            <TaskRow name="Payment File Schedule" assignee="Hari Prasad · 10–13 Jul" priority="Medium" status="Completed" />
          </div>

          <GroupLabel icon={FlaskConical} count={5}>Testing & Cutover</GroupLabel>
          <div className="space-y-1.5">
            <TaskRow innerRef={apRowRef} name="AP File Testing" meta="2/4 runs" assignee="Venkatesh · 30 Jul–02 Aug" priority="Critical" progress="2/4" status={apStatus} selected={selected === 'apfile'} />
            <TaskRow name="Go Live" assignee="Venkatesh · 13–16 Aug" priority="Critical" status={progress >= 100 ? 'Completed' : 'Pending'} />
          </div>

          {/* fade bottom */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent dark:from-[#0d0d12]" />
        </div>

        {/* Task detail side panel */}
        <AnimatePresence>
          {panelKind && (
            <motion.div
              key={panelKind}
              initial={{ x: '100%', opacity: 0.6 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0.4 }}
              transition={{ type: 'spring', stiffness: 260, damping: 30 }}
              className="absolute inset-y-0 right-0 z-20 flex w-[78%] max-w-[360px] flex-col border-l border-slate-200 bg-white p-4 shadow-2xl dark:border-white/10 dark:bg-[#0d0d12]"
              data-testid="onboarding-demo-panel"
            >
              {panelKind === 'properties' ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 dark:text-white">Properties</span>
                    <StatusChip status={propsStatus} />
                  </div>
                  <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500 dark:text-slate-400">Load all Properties / Sites that BillPortal will manage.</p>

                  <div className="mt-3 rounded-lg bg-amber-50 px-2.5 py-2 dark:bg-amber-400/10">
                    <div className="text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">Prerequisites</div>
                    <div className="mt-1 text-[10px] text-amber-800/90 dark:text-amber-200/80">Users added to the Organisation before assigning owners.</div>
                  </div>

                  <div className="mt-3 text-[9px] font-bold uppercase tracking-wide text-slate-400">How to set up</div>
                  <div className="mt-1.5 space-y-1.5">
                    {['Open UBM → Properties', 'Add manually, bulk-import CSV or use AI Migration', 'Confirm 30 / 30 properties loaded'].map((s, i) => (
                      <motion.div key={s} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.12 }} className="flex items-start gap-1.5 text-[10px] text-slate-600 dark:text-slate-300">
                        <span className="mt-[1px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[8px] font-bold text-brand">{i + 1}</span>
                        {s}
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-3 flex items-center gap-2 text-[9px] text-slate-400">
                    <span>Owner: Satish</span><span>·</span><span>Target: 21 Jun</span>
                  </div>

                  <div ref={goSetupRef} className="mt-auto flex items-center justify-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-[11px] font-bold text-white">
                    Go to Setup <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 dark:text-white">AP File Testing</span>
                    <StatusChip status={apStatus} />
                  </div>
                  <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500 dark:text-slate-400">Test the configured AP file through the customer ERP across at least four representative runs.</p>

                  {apStatus === 'Blocked' && (
                    <div className="mt-3 rounded-lg bg-red-50 px-2.5 py-2 dark:bg-red-500/10">
                      <div className="text-[9px] font-bold uppercase tracking-wide text-red-700 dark:text-red-300">Blocked reason</div>
                      <div className="mt-1 text-[10px] text-red-800/90 dark:text-red-200/80">Awaiting SAP GL-code mapping sign-off from Finance.</div>
                    </div>
                  )}

                  <div className="mt-3 text-[9px] font-bold uppercase tracking-wide text-slate-400">Updates</div>
                  <div className="mt-1.5 space-y-1.5">
                    <div className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-[10px] text-slate-600 dark:border-white/10 dark:text-slate-300">
                      <span className="font-semibold">Venkatesh</span> · Run 2/4 passed ERP import.
                    </div>
                    <AnimatePresence>
                      {apNoteAdded && (
                        <motion.div initial={{ opacity: 0, y: 8, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} className="overflow-hidden rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[10px] text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200">
                          <span className="font-semibold">Finance</span> · GL mapping approved — runs 3 & 4 clean. Unblocking. ✓
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Connected-workflow flash after Go to Setup */}
        <AnimatePresence>
          {workflow && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-white/92 backdrop-blur-sm dark:bg-[#0d0d12]/92"
            >
              <div className="flex items-center gap-2">
                {[Building2, ArrowRight, ListChecks, ArrowRight, CheckCircle2].map((I, i) => (
                  <motion.span key={i} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.14 }} className={cn('flex h-8 w-8 items-center justify-center rounded-lg', i === 4 ? 'bg-emerald-500 text-white' : i % 2 === 0 ? 'bg-brand/10 text-brand' : 'text-slate-300')}>
                    <I className="h-4 w-4" />
                  </motion.span>
                ))}
              </div>
              <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Opening Properties setup…</div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Completion banner */}
        <AnimatePresence>
          {complete && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 overflow-hidden bg-white/95 backdrop-blur-sm dark:bg-[#0d0d12]/95"
              data-testid="onboarding-demo-complete"
            >
              {/* particles */}
              {[...Array(10)].map((_, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 0, x: 0 }}
                  animate={{ opacity: [0, 1, 0], y: -60 - (i % 3) * 20, x: (i - 5) * 14 }}
                  transition={{ duration: 1.4, delay: 0.2 + (i % 5) * 0.06, ease: 'easeOut' }}
                  className={cn('absolute top-1/2 h-1.5 w-1.5 rounded-full', i % 2 ? 'bg-brand' : 'bg-emerald-500')}
                />
              ))}
              <motion.span initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 14 }} className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
                <CheckCircle2 className="h-8 w-8" />
              </motion.span>
              <div className="relative overflow-hidden">
                <div className="text-center font-display text-base font-black text-slate-900 dark:text-white">Onboarding Complete</div>
                <div className="mt-0.5 flex items-center justify-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  <Sparkles className="h-3.5 w-3.5" /> Ready for Go-Live
                </div>
                <motion.div
                  initial={{ x: '-120%' }} animate={{ x: '160%' }} transition={{ duration: 1.1, delay: 0.3, ease: 'easeInOut' }}
                  className="pointer-events-none absolute inset-y-0 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/20"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Simulated cursor */}
        {!reduced.current && (
          <motion.div
            className="pointer-events-none absolute z-50"
            animate={{ x: cursor.x, y: cursor.y }}
            transition={{ type: 'spring', stiffness: 220, damping: 26 }}
            style={{ top: 0, left: 0 }}
          >
            <div className="relative -translate-x-1 -translate-y-1">
              <MousePointer2 className="h-4 w-4 fill-slate-900 text-white drop-shadow dark:fill-white dark:text-slate-900" />
              <AnimatePresence>
                {cursor.clicking && (
                  <motion.span
                    initial={{ scale: 0, opacity: 0.6 }} animate={{ scale: 2.4, opacity: 0 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="absolute -left-2 -top-2 h-6 w-6 rounded-full border-2 border-brand"
                  />
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
